import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import { Icon } from "@/shared/ui";
import { NotificationSkillState } from "@/domains/notification/constants/notificationState";
import { Skill } from "@/domains/notification/constants/skill";
import { NotificationStatus } from "@/domains/notification/constants/status";
import { skillTranslate } from "@/domains/notification/helpers/skillTranslate";
import NotificationIndicator from "@/domains/notification/components/NotificationIndicator";

type Props = {
  className?: string;
} & NotificationSkillState;

const icons: Record<Skill, string> = {
  [Skill.Lungs]: "lungs",
};

export default function NotificationSkill({ className, skill, percent }: Props) {
  const { t } = useTranslation("notification");

  return (
    <div className={cn("flex flex-col items-center z-1", className)}>
      <motion.div
        initial={{ opacity: 0, translateY: "-100%" }}
        animate={{ opacity: 1, translateY: "none" }}
        exit={{ opacity: 0, translateY: "-100%", transition: { type: "spring", durataion: 0.3 } }}
        transition={{
          type: "spring",
          delay: 0.1,
          duration: 0.3,
        }}
        className="flex items-center gap-2.5 mt-5"
      >
        <div className="relative flex items-center justify-center">
          <NotificationIndicator
            fillPercent={percent}
            className="w-[3.75rem] h-[3.75rem]"
            status={NotificationStatus.Inherit}
          />
          <Icon
            category="notification"
            name={icons[skill]}
            className="bg-white w-[1.5rem] absolute"
          />
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, translateY: "100%" }}
        animate={{ opacity: 1, translateY: "none" }}
        exit={{ opacity: 0, translateY: "100%", transition: { type: "spring", durataion: 0.3 } }}
        transition={{
          type: "spring",
          duration: 0.3,
          delay: 0.4,
        }}
        className="mt-2.5"
      >
        <div className="text-14 font-bold text-white uppercase text-center">
          {t(skillTranslate[skill])}
        </div>
        <div className="text-12 text-white/70 font-bold mt-1 uppercase whitespace-nowrap text-center">
          {t("notification.skillUp")}
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, translateY: "100%" }}
        animate={{ opacity: 1, translateY: "none" }}
        exit={{ opacity: 0, translateY: "100%", transition: { type: "spring", durataion: 0.3 } }}
        transition={{
          type: "spring",
          duration: 0.3,
          delay: 0.7,
        }}
        className={clsx(
          "text-15 text-white font-bold mt-2.5 px-2.5 py-2 whitespace-nowrap rounded-[0.5rem]",
          "bg-gradient-to-r from-white/15 to-white/5",
        )}
      >
        <span className="text-white/70">{percent}%</span> / 100%
      </motion.div>
    </div>
  );
}
